import { useState, useEffect, useCallback } from 'react';
import { bookmarksService } from '../services/bookmarks.service';
import type { Bookmark, CreateBookmarkRequest, UpdateBookmarkRequest } from '@/types/api.types';
import { useAuthStore } from '../store/auth.store';
import { asApiError } from '@/utils/error.util';

/**
 * Hook for managing message bookmarks
 */
export const useBookmarks = (folderId?: string | null) => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuthStore();

  /**
   * Load user's bookmarks
   */
  const loadBookmarks = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const data = await bookmarksService.getUserBookmarks(folderId);
      setBookmarks(data);
    } catch (err) {
      console.error('Failed to load bookmarks:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  }, [user, folderId]);

  /**
   * Create or update a bookmark
   */
  const createBookmark = async (input: CreateBookmarkRequest) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      const bookmark = await bookmarksService.createBookmark(input);

      // Add to local state if not already present
      setBookmarks(prev => {
        const exists = prev.find(b => b.message_id === bookmark.message_id);
        if (exists) {
          return prev.map(b => b.id === bookmark.id ? bookmark : b);
        }
        return [bookmark, ...prev];
      });

      return bookmark;
    } catch (err) {
      console.error('Failed to create bookmark:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to create bookmark');
      throw err;
    }
  };

  /**
   * Remove a bookmark
   */
  const removeBookmark = async (messageId: string) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      await bookmarksService.removeBookmark(messageId);

      // Remove from local state
      setBookmarks(prev => prev.filter(b => b.message_id !== messageId));
    } catch (err) {
      console.error('Failed to remove bookmark:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to remove bookmark');
      throw err;
    }
  };

  /**
   * Update bookmark (move to folder, change note)
   */
  const updateBookmark = async (messageId: string, input: UpdateBookmarkRequest) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      const updated = await bookmarksService.updateBookmark(messageId, input);

      // Update local state
      setBookmarks(prev => prev.map(b => b.message_id === messageId ? updated : b));

      return updated;
    } catch (err) {
      console.error('Failed to update bookmark:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to update bookmark');
      throw err;
    }
  };

  /**
   * Check if a message is bookmarked
   */
  const isBookmarked = async (messageId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const result = await bookmarksService.isBookmarked(messageId);
      return result.bookmarked;
    } catch (err) {
      console.error('Failed to check bookmark status:', err);
      return false;
    }
  };

  /**
   * Toggle bookmark on/off
   */
  const toggleBookmark = async (messageId: string, folderId?: string) => {
    const bookmark = bookmarks.find(b => b.message_id === messageId);

    if (bookmark) {
      await removeBookmark(messageId);
    } else {
      await createBookmark({ messageId, folderId });
    }
  };

  // Load bookmarks on mount and when folder changes
  useEffect(() => {
    loadBookmarks();
  }, [loadBookmarks]);

  return {
    bookmarks,
    loading,
    error,
    createBookmark,
    removeBookmark,
    updateBookmark,
    isBookmarked,
    toggleBookmark,
    refresh: loadBookmarks,
  };
};
